import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft,
  BookOpen,
  Upload,
  AlertTriangle,
  MessageSquare,
  CheckCircle2,
  Send,
} from "lucide-react";

import narratorService from "@/services/narratorService";
import authService from "@/services/authService";
import NarratorHeader from "@/components/narrator/NarratorHeader";
import ProgressSlider from "@/components/narrator/ProgressSlider";
import ChapterUploadCard from "@/components/narrator/ChapterUploadCard";
import QCIssueCard from "@/components/narrator/QCIssueCard";

export default function ProjectWorkspace() {
  const navigate = useNavigate();
  const { projectId } = useParams();

  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [activeTab, setActiveTab] = useState("chapters");

  useEffect(() => {
    loadWorkspace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const loadWorkspace = async () => {
    try {
      setLoading(true);
      const data = await narratorService.getProjectWorkspace(projectId);
      setWorkspace(data);
    } catch (error) {
      console.error("Error loading workspace:", error);
      if (error.response?.status === 401) {
        authService.clearTokens();
        navigate("/login");
      } else if (error.response?.status === 404) {
        toast.error("Project not found");
        navigate("/narrator/projects");
      } else if (error.response?.status === 403) {
        toast.error("Access denied");
        navigate("/narrator/projects");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProgressUpdate = async (newProgress) => {
    try {
      await narratorService.updateProgress(projectId, newProgress);
      toast.success("Progress updated and synced across all pages");
      await loadWorkspace();
    } catch (error) {
      console.error("Error updating progress:", error);
      toast.error("Failed to update progress");
    }
  };

  const handleChapterUpload = async (chapterId) => {
    try {
      await narratorService.uploadChapterAudio(projectId, chapterId);
      toast.success("Chapter audio uploaded (stub)");
      await loadWorkspace();
    } catch (error) {
      console.error("Error uploading chapter:", error);
      toast.error("Failed to upload chapter");
    }
  };

  const handleResolveQCIssue = async (issueId) => {
    try {
      await narratorService.resolveQCIssue(issueId);
      toast.success("Issue marked as resolved");
      await loadWorkspace();
    } catch (error) {
      console.error("Error resolving issue:", error);
      toast.error("Failed to resolve issue");
    }
  };

  const handleSubmitForQC = async () => {
    try {
      await narratorService.submitForQC(projectId);
      toast.success("Project submitted for QC review");
      await loadWorkspace();
    } catch (error) {
      console.error("Error submitting for QC:", error);
      toast.error("Failed to submit for QC");
    }
  };

  const statusConfig = {
    ASSIGNED: { label: "Assigned", className: "bg-blue-100 text-blue-800" },
    RECORDING: { label: "Recording", className: "bg-purple-100 text-purple-800" },
    SUBMITTED_FOR_QC: { label: "In QC Review", className: "bg-yellow-100 text-yellow-800" },
    QC_PASSED: { label: "QC Passed", className: "bg-green-100 text-green-800" },
    COMPLETED: { label: "Completed", className: "bg-green-100 text-green-800" },
  };

  if (loading) {
    return (
      <div className="relative min-h-screen overflow-hidden">

  {/* Full Page Background */}
  <img
    src="/images/dashboard-bg.png"
    alt="Background"
    className="absolute inset-0 w-full h-full object-cover -z-10"
  />

  {/* Light Overlay */}
  <div className="absolute inset-0 bg-black/10 -z-10"></div>
 

        <NarratorHeader />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading workspace...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!workspace) {
    return null;
  }

  const { project, book, chapters, uploads, qc_issues, progress } = workspace;
  const config = statusConfig[project.status] || statusConfig.ASSIGNED;
  const openIssues = qc_issues.filter((i) => i.status === "OPEN");
  const recordingPct = progress?.recording_pct || 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="project-workspace">
      <NarratorHeader />

     <main className="container mx-auto px-6 py-8 relative z-10">

        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/narrator/projects")}
          className="mb-4"
          data-testid="back-to-projects-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </Button>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-8">
          <div>
            <h1
              className="text-4xl font-bold mb-2"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              {book.title || "Untitled Book"}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={config.className}>{config.label}</Badge>
              <Badge variant="outline">{book.language || "English"}</Badge>
              <Badge variant="outline">{project.assignment_type}</Badge>
              {project.due_date && (
                <Badge variant="outline">
                  Due: {new Date(project.due_date).toLocaleDateString()}
                </Badge>
              )}
            </div>
          </div>

          {project.status === "RECORDING" && recordingPct === 100 && (
            <Button
              onClick={handleSubmitForQC}
              className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500"
              data-testid="submit-for-qc-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit for QC Review
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="chapters" data-testid="tab-chapters">
                  <Upload className="w-4 h-4 mr-2" />
                  Chapters ({chapters.length})
                </TabsTrigger>
                <TabsTrigger value="qc" data-testid="tab-qc">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  QC Issues ({openIssues.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="chapters" className="space-y-3">
                {chapters.length > 0 ? (
                  chapters
                    .sort((a, b) => a.chapter_number - b.chapter_number)
                    .map((chapter) => (
                      <ChapterUploadCard
                        key={chapter.id}
                        chapter={chapter}
                        uploads={uploads}
                        onUpload={handleChapterUpload}
                      />
                    ))
                ) : (
                  <Card className="bg-white border-2 border-slate-200">
                    <CardContent className="py-12 text-center">
                      <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p className="text-slate-600">
                        No chapters assigned yet. Check back later.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="qc" className="space-y-3">
                {qc_issues.length > 0 ? (
                  qc_issues.map((issue) => (
                    <QCIssueCard
                      key={issue.id}
                      issue={issue}
                      onResolve={handleResolveQCIssue}
                    />
                  ))
                ) : (
                  <Card className="bg-white border-2 border-slate-200">
                    <CardContent className="py-12 text-center">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-500" />
                      <p className="text-slate-600">No QC issues! Great work!</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Progress */}
            <Card className="bg-white border-2 border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle
                  className="text-lg"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Recording Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ProgressSlider
                  value={recordingPct}
                  onUpdate={handleProgressUpdate}
                  disabled={project.status === "COMPLETED" || project.status === "QC_PASSED"}
                />
                <p className="text-xs text-slate-500 mt-2">
                  Updates sync to Dashboard, Participate, and Publisher pages
                </p>
              </CardContent>
            </Card>

            {/* Book Info */}
            <Card className="bg-white border-2 border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle
                  className="text-lg"
                  style={{ fontFamily: "Playfair Display, serif" }}
                >
                  Book Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="text-slate-600">Author:</span>{" "}
                  <span className="text-black">{book.author || "Unknown"}</span>
                </div>
                <div>
                  <span className="text-slate-600">Genre:</span>{" "}
                  <span className="text-black">{book.genre || "General"}</span>
                </div>
                <div>
                  <span className="text-slate-600">Country:</span>{" "}
                  <span className="text-black">{book.country || "Unknown"}</span>
                </div>
                {book.description_short && (
                  <div className="pt-2 border-t">
                    <p className="text-slate-600 text-xs">{book.description_short}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Guidelines */}
            {project.guidelines && (
              <Card className="bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 border-2 border-purple-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Recording Guidelines</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-700">{project.guidelines}</p>
                </CardContent>
              </Card>
            )}

            {/* Quick Stats */}
            <Card className="bg-white border-2 border-slate-200">
              <CardContent className="py-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-purple-600">
                      {uploads.length}
                    </p>
                    <p className="text-xs text-slate-600">Uploads</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">
                      {openIssues.length}
                    </p>
                    <p className="text-xs text-slate-600">Open Issues</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
